/**
 * WideBot WebChat embed loader (v2 — single init mode).
 *
 * Built as a separate Vite IIFE bundle so it can be served stand-alone from S3
 * and load INSTANTLY without React/Redux. It only fetches the heavy bundle
 * (`botchat.umd.cjs`) once the user clicks the launcher.
 *
 * Source: TypeScript so we get type-checking + can read `import.meta.env.VITE_*`
 * for per-environment URLs at build time.
 *
 * Per-environment outputs (set by `vite build --mode <env>`):
 *   - dev  → uses gateway-dev + /webchat-dev/v2/ S3 path
 *   - qc   → uses gateway-qc  + /webchat-qc/v2/  S3 path
 *   - prod → uses gateway     + /webchat-live/v2/ S3 path
 *
 * Customer integration tag (one line per environment):
 *   <script src="https://widebot-webchat.s3.amazonaws.com/webchat-live/v2/widebot.js?botConfigs=<encryptedBotId>"></script>
 *
 * Contract preserved (REWRITE_SPEC.md §2): DOM IDs, localStorage keys, cookies,
 * window.widebot, window._widebot_, window.Android, window.webkit. Removed:
 * encodeObject/decodeString codec, Zendesk zopim handover.
 */

// ---------- env-baked URLs (resolved at build time, NOT runtime) -----------
const ENV = import.meta.env.VITE_BUILD_ENV ?? "dev";
const GATEWAY_BASE_URL = import.meta.env.VITE_GATEWAY_BASE_URL!;
const BUNDLE_JS_URL = import.meta.env.VITE_BUNDLE_JS_URL!;
const BUNDLE_CSS_URL = import.meta.env.VITE_BUNDLE_CSS_URL!;
const FINGERPRINT_URL = import.meta.env.VITE_FINGERPRINT_URL!;

// ---------- DOM IDs (must match src/constants/domIds.ts) -------------------
const DOM = {
  OUTER: "widebot-chat-container",
  INNER: "inner-chat-container",
  LAUNCHER: "kaec-chat-button",
  TOOLTIP: "tooltip",
  LOADER: "loader",
  BOT_HEADER: "botHeader",
  SCRIPT_TAG: "chatbotScript",
  CSS_TAG: "botchat-css",
} as const;

const WIDEBOT_KEY = "_widebot_";

// ---------- minimal type for the resolved config ---------------------------
interface ResolvedConfig {
  pageId?: string;
  botId?: string;
  botToken: string;
  directLineURL: string;
  botName?: string;
  botAvatar?: string;
  botHeader?: string;
  buttonImageSrc?: string;
  mainColor?: string;
  Font?: string;
  WelcomeMessage?: string;
  userName?: string;
  configurations?: Array<{ Type: number; Metadata?: string }>;
  // Type-9 metadata fields, after `applyType9Metadata`:
  HasWaterMark?: boolean;
  EnableForm?: boolean;
  RequirePhoneNumber?: boolean;
  RequireEmail?: boolean;
  EnableWelcomeMessage?: boolean;
  EnableVoice?: boolean;
  EnableAttach?: boolean;
  EnableLocation?: boolean;
  EnableEmojis?: boolean;
  RTL?: boolean;
  isChangeLanguageSupported?: boolean;
  isGetStarted?: boolean;
  DisableUpload?: boolean;
  openedOnInit?: boolean;
  GetStartedMessage?: string;
  GetStartedFlowId?: string | number;
  BackgroundImage?: string;
  GetStartedImage?: string;
  HeaderImage?: string;
  BotDisplayName?: string;
}

// minimal Fingerprint2 typing (from the global script we lazy-load)
declare const Fingerprint2: {
  get(cb: (components: Array<{ value: unknown }>) => void): void;
  x64hash128(input: string, seed: number): string;
};

// ---------- internal state -------------------------------------------------
let encodedBotId: string | null = null;
let resolvedConfig: ResolvedConfig | null = null;
let bundleLoaded = false;
let allowAutoOpen = true;
let isMobileFullScreen = false;
let alreadySentFlow = false;

let bootResolve: (v: boolean) => void;
const isWebChatLoaded = new Promise<boolean>((resolve) => {
  bootResolve = resolve;
});

const w = window as unknown as Record<string, any>;

// ---------- public API -----------------------------------------------------
w[WIDEBOT_KEY] = w[WIDEBOT_KEY] || {};
w[WIDEBOT_KEY].isWebChatLoaded = isWebChatLoaded;
w[WIDEBOT_KEY].isDirectLineConnected = false;
w[WIDEBOT_KEY].SetAutoSendFlow = SetAutoSendFlow;
w[WIDEBOT_KEY].fullMobileScreenWebChat = fullMobileScreenWebChat;
w[WIDEBOT_KEY].setUserNameAndEmail = setUserNameAndEmail;
w[WIDEBOT_KEY].deleteUserNameAndEmail = deleteUserNameAndEmail;
w[WIDEBOT_KEY].clearUserHistory = clearUserHistory;

w.widebot = {
  init,
  initConfigs,
  refreshJS: () => refreshJS(true),
  refreshCSS: () => refreshCSS(true),
  loadLocals,
  fullScreenOnSmall,
};

// ---------- native bridge stubs (decision §10.3) ---------------------------
w.Android = w.Android || {
  onWebchatEvent() {
    console.warn("[WideBot] Android bridge not available");
  },
};
w.webkit = w.webkit || {
  messageHandlers: {
    jsHandler: {
      postMessage() {
        console.warn("[WideBot] iOS bridge not available");
      },
    },
  },
};

// ---------- entry: read script src + autoboot ------------------------------
try {
  const src = document.currentScript ? (document.currentScript as HTMLScriptElement).src : "";
  if (src) {
    const url = new URL(src);
    encodedBotId = url.searchParams.get("botConfigs");
  }
} catch (e) {
  console.error("[WideBot] failed to read script src", e);
}

window.addEventListener("load", () => {
  if (encodedBotId && allowAutoOpen) {
    void init(encodedBotId);
  }
});

// ---------- init flow ------------------------------------------------------

// M7: Retry constants for exponential backoff on config fetch failure.
// 5 attempts: 250 ms → 500 ms → 1 000 ms → 2 000 ms → 4 000 ms (then abort).
// Cap is 8 000 ms but 2^(4-1)*250 = 2 000 ms is the last delay before the
// 5th attempt — after which we give up and show the failure UI (REWRITE_SPEC §2.1).
const RETRY_ATTEMPTS = 5;
const RETRY_BASE_MS = 250;
const RETRY_CAP_MS = 8_000;

/** Delay helper — resolves after `ms` milliseconds. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * M7: Validate that the config response contains the required fields mandated
 * by REWRITE_SPEC §3 / src/types/config.ts. `botToken` and `directLineURL`
 * are the two load-bearing fields — without them the DirectLine connection
 * cannot be established. Log and return `false` on failure; callers must NOT
 * throw past this boundary so the host page is never crashed.
 */
function isValidConfig(cfg: unknown): cfg is ResolvedConfig {
  if (!cfg || typeof cfg !== "object") {
    console.error("[WideBot] config is not an object", cfg);
    return false;
  }
  const c = cfg as Record<string, unknown>;
  if (typeof c["botToken"] !== "string" || !c["botToken"]) {
    console.error("[WideBot] config missing required field: botToken");
    return false;
  }
  if (typeof c["directLineURL"] !== "string" || !c["directLineURL"]) {
    console.error("[WideBot] config missing required field: directLineURL");
    return false;
  }
  return true;
}

/**
 * M7: Render the failure launcher — replaces the normal launcher image with a
 * plain `?` glyph so users can see that something went wrong. Uses inline
 * style on the existing launcher element (no new <style> blocks — CLAUDE.md
 * rule). The tooltip "WideBot chat unavailable" is a native `title` attribute
 * (works across all browsers without JS). No SVG — Unicode only (CLAUDE.md).
 */
function showFailureUI(): void {
  // Remove any existing outer widget (e.g., from a previous failed attempt).
  document.getElementById(DOM.OUTER)?.remove();

  const outer = document.createElement("div");
  outer.id = DOM.OUTER;
  outer.style.cssText =
    "position:fixed;bottom:0;right:20px;width:80px;z-index:999999;direction:ltr;";

  const btn = document.createElement("div");
  btn.id = DOM.LAUNCHER;
  btn.title = "WideBot chat unavailable";
  btn.setAttribute("role", "img");
  btn.setAttribute("aria-label", "WideBot chat unavailable");
  btn.textContent = "?";
  btn.style.cssText =
    "position:absolute;bottom:10px;right:10px;width:70px;height:70px;" +
    "border-radius:50%;background:#ccc;color:#555;cursor:default;" +
    "display:flex;align-items:center;justify-content:center;" +
    "font-size:32px;font-family:sans-serif;font-weight:bold;" +
    "z-index:999999999;filter:drop-shadow(0 0 1px gray);";

  outer.appendChild(btn);
  document.body.appendChild(outer);
}

async function init(botId: string): Promise<void> {
  encodedBotId = botId;
  // CORS: the gateway must respond with `Access-Control-Allow-Origin` for the
  // S3 origin (widebot-webchat.s3.amazonaws.com). This is a DevOps concern —
  // the fetch itself does not set custom headers, so a simple CORS preflight
  // is not triggered. Verify via `curl -I -H "Origin: ..." <configsURL>`.
  const configsURL = `${GATEWAY_BASE_URL}/backend/WebChatConfig/${encodeURIComponent(botId)}`;

  let lastError: unknown;
  for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(configsURL);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      // M7: Guard against non-JSON responses (e.g., gateway returning an HTML
      // error page). `response.json()` throws on parse failure — we catch it
      // and abort cleanly without crashing the host page.
      let configs: unknown;
      try {
        configs = await response.json();
      } catch (parseErr) {
        console.error("[WideBot] config response is not valid JSON", parseErr);
        showFailureUI();
        return; // Malformed response — abort immediately, no retry.
      }

      // M7: Validate required fields before handing off to initConfigs.
      if (!isValidConfig(configs)) {
        // isValidConfig already logged the specific missing field.
        showFailureUI();
        return; // Structural problem — retrying won't help.
      }

      initConfigs(configs);
      return; // Success — exit the retry loop.
    } catch (err) {
      lastError = err;
      if (attempt < RETRY_ATTEMPTS) {
        // Exponential backoff capped at RETRY_CAP_MS (Spec M7 §deliverables).
        const backoff = Math.min(RETRY_BASE_MS * Math.pow(2, attempt - 1), RETRY_CAP_MS);
        console.warn(`[WideBot] config fetch failed (attempt ${attempt}/${RETRY_ATTEMPTS}), retrying in ${backoff}ms`, err);
        await delay(backoff);
      }
    }
  }

  // All attempts exhausted — surface the error and show failure UI.
  console.error("[WideBot] failed to load config after", RETRY_ATTEMPTS, "attempts", lastError);
  showFailureUI();
}

function initConfigs(configs: ResolvedConfig): void {
  resolvedConfig = applyType9Metadata(configs);
  const pageId = resolvedConfig.pageId || resolvedConfig.botId;
  if (!pageId) {
    console.error("[WideBot] config has no pageId/botId");
    return;
  }
  w[WIDEBOT_KEY].pageId = pageId;
  w[WIDEBOT_KEY].mainColor = resolvedConfig.mainColor || "#FF5100";
  w[WIDEBOT_KEY].Font = resolvedConfig.Font || "sans-serif";

  const FEATURE_FLAGS = [
    "HasWaterMark",
    "EnableForm",
    "RequirePhoneNumber",
    "RequireEmail",
    "EnableWelcomeMessage",
    "EnableVoice",
    "EnableAttach",
    "EnableLocation",
    "EnableEmojis",
    "RTL",
    "isChangeLanguageSupported",
    "isGetStarted",
    "DisableUpload",
    "openedOnInit",
  ] as const;
  for (const flag of FEATURE_FLAGS) {
    if (resolvedConfig[flag as keyof ResolvedConfig]) {
      w[WIDEBOT_KEY][flag] = true;
    }
  }
  if (resolvedConfig.GetStartedMessage) w[WIDEBOT_KEY].GetStartedMessage = resolvedConfig.GetStartedMessage;
  if (resolvedConfig.GetStartedFlowId !== undefined && resolvedConfig.GetStartedFlowId !== "") {
    w[WIDEBOT_KEY].GetStartedFlowId = resolvedConfig.GetStartedFlowId;
  }
  if (resolvedConfig.BackgroundImage) w[WIDEBOT_KEY].BackgroundImage = resolvedConfig.BackgroundImage;
  if (resolvedConfig.GetStartedImage) w[WIDEBOT_KEY].GetStartedImage = resolvedConfig.GetStartedImage;
  if (resolvedConfig.HeaderImage) w[WIDEBOT_KEY].HeaderImage = resolvedConfig.HeaderImage;
  if (resolvedConfig.botHeader) w[WIDEBOT_KEY].botHeader = resolvedConfig.botHeader;
  if (resolvedConfig.BotDisplayName) w[WIDEBOT_KEY].BotDisplayName = resolvedConfig.BotDisplayName;

  buildWidgetDOM(resolvedConfig);

  if (w[WIDEBOT_KEY].openedOnInit) toggleChat();
  if (w[WIDEBOT_KEY].autoSendFlowId) {
    const sent = JSON.parse(localStorage.getItem("sentFlows") || "[]") as string[];
    alreadySentFlow = sent.includes(w[WIDEBOT_KEY].autoSendFlowId);
    if (!alreadySentFlow) toggleChat();
  }
}

function applyType9Metadata(config: ResolvedConfig): ResolvedConfig {
  const row = config.configurations?.find((r) => r.Type === 9 && r.Metadata);
  if (!row?.Metadata) return config;
  try {
    const metadata = JSON.parse(row.Metadata) as Partial<ResolvedConfig>;
    return { ...config, ...metadata };
  } catch {
    return config;
  }
}

// ---------- DOM scaffolding ------------------------------------------------
function buildWidgetDOM(config: ResolvedConfig): void {
  document.getElementById(DOM.OUTER)?.remove();

  const outer = document.createElement("div");
  outer.id = DOM.OUTER;
  outer.style.cssText =
    "position:fixed;bottom:0;right:20px;width:400px;z-index:999999;direction:ltr;";

  if (config.WelcomeMessage && config.EnableWelcomeMessage) {
    outer.appendChild(buildWelcomeTooltip(config.WelcomeMessage));
  }

  const inner = document.createElement("div");
  inner.id = DOM.INNER;
  inner.style.cssText =
    "width:100%;border:none;border-radius:12px;height:550px;max-height:calc(100vh - 90px);" +
    "background:#fff;position:absolute;bottom:70px;right:20px;box-shadow:0 1rem 3rem rgba(0,0,0,.175) !important;" +
    "overflow:hidden;transition:all 200ms;z-index:99999999999;display:none;";
  if (isBrightColor(w[WIDEBOT_KEY].mainColor)) inner.classList.add("dark");
  outer.appendChild(inner);

  const loader = document.createElement("div");
  loader.id = DOM.LOADER;
  loader.style.cssText =
    "border:8px solid #f1f6fb;border-top-color:" +
    (config.mainColor || "#ff5100") +
    ";border-radius:50%;width:50px;height:50px;margin:50% auto auto;";
  loader.animate(
    [{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }],
    { duration: 1000, iterations: Infinity, easing: "cubic-bezier(0.25,0.1,0.15,0.68)" },
  );
  inner.appendChild(loader);

  const btn = document.createElement("img");
  btn.id = DOM.LAUNCHER;
  btn.src = resolveLauncherSrc(config);
  btn.alt = "Open chat";
  btn.style.cssText =
    "position:absolute;bottom:10px;right:10px;width:70px;height:70px;cursor:pointer;" +
    "z-index:999999999;filter:drop-shadow(0 0 1px gray);display:none;border-radius:50%;object-fit:contain;";
  btn.addEventListener("click", toggleChat);
  outer.appendChild(btn);

  document.body.appendChild(outer);
  btn.style.display = "block";

  handleResize();
  if (isMobileFullScreen) {
    enterFullscreen(outer, inner, btn);
    toggleChat();
  } else {
    window.addEventListener("resize", handleResize);
  }
}

function buildWelcomeTooltip(message: string): HTMLDivElement {
  const dismissed = localStorage.getItem(`dismiss-btn-${encodedBotId}`) === "true";
  const popup = document.createElement("div");
  popup.id = DOM.TOOLTIP;
  popup.dir = "ltr";
  popup.style.cssText =
    "font-family:sans-serif !important;position:fixed;cursor:pointer;z-index:999999999;" +
    "right:40px;bottom:85px;display:flex;flex-direction:column;align-items:end;margin:0 .5rem;";

  if (!dismissed) {
    const bubble = document.createElement("span");
    bubble.style.cssText =
      "background:#fff;border-radius:10px;box-shadow:0 0 10px rgba(0,0,0,.1);" +
      "color:#000;padding:15px;position:relative;max-width:38ch;";
    bubble.textContent = message;
    bubble.addEventListener("click", toggleChat);

    const dismiss = document.createElement("button");
    dismiss.style.cssText =
      "position:relative;height:40px;width:120px;padding:10px 20px;margin-bottom:5px;" +
      "border-radius:24px;visibility:hidden;border:none;background:#8AA1AC;color:#fff;cursor:pointer;";
    dismiss.textContent = "Dismiss ✕";
    dismiss.addEventListener("click", () => {
      localStorage.setItem(`dismiss-btn-${encodedBotId}`, "true");
      popup.style.display = "none";
      const inner = document.getElementById(DOM.INNER);
      if (inner) inner.style.display = "none";
    });

    popup.appendChild(dismiss);
    popup.appendChild(bubble);
    popup.onmouseover = () => (dismiss.style.visibility = "visible");
    popup.onmouseout = () => (dismiss.style.visibility = "hidden");
  }
  return popup;
}

// ---------- bundle load + chat boot ----------------------------------------

// Loader self-update: widebot.js is served from S3+CloudFront with a short
// Cache-Control max-age (set by DevOps). Browsers re-validate via
// If-Modified-Since / ETag, so all customers transparently pick up new loader
// versions without any code change here. See M9 deployment notes.
function toggleChat(): void {
  const tooltip = document.getElementById(DOM.TOOLTIP);
  if (tooltip) tooltip.style.display = "none";

  if (!bundleLoaded) {
    const inner = document.getElementById(DOM.INNER);
    if (!inner) return;
    // Show the container — loader is already in the DOM from buildWidgetDOM(), spinning.
    inner.style.display = "block";

    loadScript(FINGERPRINT_URL, undefined, () => {
      loadScript(BUNDLE_JS_URL, DOM.SCRIPT_TAG, () => {
        loadCSS(BUNDLE_CSS_URL, DOM.CSS_TAG);
        bootChat((conversationId) => {
          bundleLoaded = true;
          bootResolve(true);
          // M7: Diagnostic event — lets host analytics correlate page sessions
          // to WideBot conversations. Spec §M7 deliverables. Fired once, after
          // the React bundle mounts and the UUID-based conversationId is known.
          // Host pages subscribe with: window.addEventListener("widebot:ready", e => ...)
          const pageId = w[WIDEBOT_KEY].pageId as string | undefined;
          window.dispatchEvent(
            new CustomEvent("widebot:ready", { detail: { pageId, conversationId } }),
          );
          // Window is already open from loader display, don't toggle it closed
          // if (w[WIDEBOT_KEY].autoSendFlowId && !alreadySentFlow) {
          //   setTimeout(openChat, 1000);
          // } else {
          //   openChat();
          // }
        });
      });
    });
  } else {
    openChat();
  }
}

function openChat(): void {
  const inner = document.getElementById(DOM.INNER);
  if (!inner) return;
  const visible = inner.style.display === "block";
  inner.style.display = visible ? "none" : "block";
}

/**
 * Boot the React bundle.
 *
 * M7: callback now receives the `conversationId` (the UUID-based identifier
 * built from browser fingerprint + timestamp) so the caller can include it in
 * the `widebot:ready` diagnostic event detail.
 */
function bootChat(cb: (conversationId: string) => void): void {
  if (!resolvedConfig) return;
  setIdentityCookies();
  const pageId = w[WIDEBOT_KEY].pageId as string;

  Fingerprint2.get((components) => {
    const uuidKey = `CID_1052018_WIDEBOTUUID${pageId}`;
    let uuid = localStorage.getItem(uuidKey);
    if (!uuid) {
      uuid = Fingerprint2.x64hash128(
        components.map((c) => String(c.value)).join() + Date.now(),
        31,
      );
      localStorage.setItem(uuidKey, uuid);
    }
    document.cookie = `CID_1052018_WIDEBOT=${pageId}-${uuid};path=/;Secure`;

    const qp = new URLSearchParams(window.location.search);
    if (qp.get("autoClose") === "true") window.close();

    const userName = readUserName(pageId, resolvedConfig!);
    const inner = document.getElementById(DOM.INNER);
    if (!inner) return;
    if (!window.BotChat) {
      console.error("[WideBot] BotChat global missing — bundle failed to expose UMD entry");
      return;
    }
    window.BotChat.App(
      {
        directLine: {
          token: resolvedConfig!.botToken,
          domain: resolvedConfig!.directLineURL,
          webSocket: true,
        },
        user: { id: `${pageId}-${uuid}`, name: userName },
        bot: { id: pageId, name: resolvedConfig!.botName },
        enableForm: resolvedConfig!.EnableForm ?? false,
        requirePhoneNumber: resolvedConfig!.RequirePhoneNumber ?? false,
        requireEmail: resolvedConfig!.RequireEmail ?? false,
        rtl: resolvedConfig!.RTL ?? false,
        hasWaterMark: resolvedConfig!.HasWaterMark ?? true,
        avatarUrl: resolvedConfig!.botAvatar,
        enableLocation: !!resolvedConfig!.EnableLocation,
        enableVoice: resolvedConfig!.EnableVoice !== false,
        enableAttach: resolvedConfig!.EnableAttach !== false && !resolvedConfig!.DisableUpload,
        enableEmojis: resolvedConfig!.EnableEmojis !== false,
        showLanguageToggle: !!resolvedConfig!.isChangeLanguageSupported,
        locale: resolvedConfig!.RTL ? "ar" : "en",
        pageId,
      },
      inner,
    );

    const span = document.getElementById(DOM.BOT_HEADER);
    if (span) {
      span.textContent = resolvedConfig!.botHeader || resolvedConfig!.BotDisplayName || "";
    }

    // Pass the conversationId (uuid) up so the caller can include it in the
    // widebot:ready event. The uuid is the stable per-device identifier used
    // for DirectLine user identity, which is the closest analogue to a
    // conversationId that the loader has access to at mount time.
    cb(`${pageId}-${uuid}`);
  });
}

function readUserName(pageId: string, config: ResolvedConfig): string {
  try {
    const raw = localStorage.getItem(`userMeta${pageId}`);
    if (raw) {
      const cached = JSON.parse(raw) as { name?: string };
      if (cached?.name) return cached.name;
    }
  } catch {
    /* ignore */
  }
  return config.userName || "user";
}

function setIdentityCookies(): void {
  const url = window.location !== window.parent.location ? document.referrer : document.location.href;
  const domain = extractOrigin(url);
  document.cookie = `UD_09091995_WIDEBOT=${domain};path=/;Secure`;
  document.cookie = "EUD_24091996_WIDEBOT= ;path=/;Secure";
}

function extractOrigin(href: string): string {
  if (href.indexOf("://") > -1) {
    const parts = href.split("/");
    return `${parts[0]}//${parts[2].split("?")[0]}`;
  }
  return href.split("/")[0].split("?")[0];
}

// ---------- responsive -----------------------------------------------------
function handleResize(): void {
  const outer = document.getElementById(DOM.OUTER);
  const inner = document.getElementById(DOM.INNER);
  if (!outer || !inner) return;
  const mq = window.matchMedia("(max-width: 570px)");
  if (mq.matches) {
    inner.style.width = "auto";
    inner.style.left = "34px";
    outer.style.width = "100%";
    outer.style.right = "20px";
  } else {
    inner.style.width = "100%";
    inner.style.left = "initial";
    outer.style.width = "400px";
    outer.style.right = "50px";
  }
}

function fullScreenOnSmall(): void {
  if (window.screen.width <= 425) {
    setTimeout(() => {
      const outer = document.getElementById(DOM.OUTER);
      const inner = document.getElementById(DOM.INNER);
      if (!outer || !inner) return;
      outer.style.cssText += "inset:0 !important;";
      inner.style.cssText += "inset:0 !important;height:100%;";
    }, 500);
  }
}

function enterFullscreen(outer: HTMLElement, inner: HTMLElement, btn: HTMLElement | null): void {
  outer.classList.add("full-size-mobile");
  Object.assign(outer.style, {
    width: "100%",
    height: "100%",
    bottom: "0",
    right: "0",
    left: "initial",
  });
  Object.assign(inner.style, {
    width: "100%",
    height: "100%",
    right: "0",
    left: "0",
    bottom: "0",
    borderRadius: "0",
    maxHeight: "100%",
  });
  if (btn) btn.remove();
}

// ---------- helpers used by host pages -------------------------------------
function setUserNameAndEmail(name: string, email: string): void {
  const pageId = w[WIDEBOT_KEY].pageId as string | undefined;
  if (!pageId) return;
  localStorage.setItem(`userMeta${pageId}`, JSON.stringify({ name }));
  localStorage.setItem(`userEmail${pageId}`, JSON.stringify({ email }));
  if (encodedBotId) void init(encodedBotId);
}

function deleteUserNameAndEmail(): void {
  setUserNameAndEmail("user", "");
}

function clearUserHistory(): void {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && (k.includes("activities") || k.includes("sentFlows"))) keys.push(k);
  }
  keys.forEach((k) => localStorage.removeItem(k));
  if (encodedBotId) void init(encodedBotId);
}

function SetAutoSendFlow(flowId = ""): void {
  if (!encodedBotId) return;
  w[WIDEBOT_KEY].autoSendFlowId = flowId;
  allowAutoOpen = false;
  void init(encodedBotId);
}

function fullMobileScreenWebChat(): void {
  if (!encodedBotId) return;
  isMobileFullScreen = true;
  allowAutoOpen = false;
  void init(encodedBotId);
}

// ---------- dev helpers ----------------------------------------------------
let devBundleJsUrl = BUNDLE_JS_URL;
let devBundleCssUrl = BUNDLE_CSS_URL;

function refreshJS(reinit: boolean): void {
  document.getElementById(DOM.SCRIPT_TAG)?.remove();
  document.getElementById(DOM.OUTER)?.remove();
  devBundleJsUrl = "http://localhost:8000/dist/botchat.umd.cjs";
  bundleLoaded = false;
  if (reinit && resolvedConfig) initConfigs(resolvedConfig);
}

function refreshCSS(reinit: boolean): void {
  document.getElementById(DOM.CSS_TAG)?.remove();
  devBundleCssUrl = "http://localhost:8000/dist/botchat.css";
  if (reinit && resolvedConfig) initConfigs(resolvedConfig);
}

function loadLocals(): void {
  window.botchatDebug = true;
  refreshCSS(false);
  refreshJS(false);
  if (resolvedConfig) initConfigs(resolvedConfig);
}

// ---------- low-level helpers ----------------------------------------------
function loadScript(src: string, id: string | undefined, cb?: () => void): void {
  const s = document.createElement("script");
  s.type = "text/javascript";
  if (id) s.id = id;
  s.onload = () => cb?.();
  s.onerror = () => console.error("[WideBot] failed to load script", src);
  s.src = src;
  document.head.appendChild(s);
}

function loadCSS(src: string, id: string): void {
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.type = "text/css";
  l.href = src;
  l.id = id;
  document.head.appendChild(l);
}

function isBrightColor(hex: string | undefined): boolean {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return Math.round((r * 299 + g * 587 + b * 114) / 1000) > 148;
}

function resolveLauncherSrc(config: ResolvedConfig): string {
  const fallback = "https://widebot-webchat.s3.amazonaws.com/shared/widebot.png";
  if (config.buttonImageSrc && /^https?:\/\//.test(config.buttonImageSrc)) {
    return config.buttonImageSrc;
  }
  return fallback;
}

// ---------- cross-tab lock (decision §10.7) --------------------------------
if (navigator?.locks?.request) {
  let lockResolver: (() => void) | null = null;
  const lockPromise = new Promise<void>((res) => {
    lockResolver = res;
  });
  // intentionally never call lockResolver — the lock is held for the lifetime of the tab
  void lockResolver;
  void navigator.locks.request("_widebot_", { mode: "shared" }, () => lockPromise);
}

// dev-helpers must be referenced or TS noUnusedLocals will complain
void devBundleJsUrl;
void devBundleCssUrl;
void ENV;
